import { Stack } from "@mui/material";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  AutoCompleteItem,
  DropdownInputField,
  DropdownItem,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React from "react";
import { useTranslation } from "react-i18next";
import { SurveyRuleConditionModel } from "../../models/SurveyRuleConditionModel";
import { useSurveyRuleStore } from "../../stores/Hooks";
import { SurveyRuleKeyInputField } from "../inputs/SurveyRuleKeyInputField";
import { SurveyRuleValueInputField } from "../inputs/SurveyRuleValueInputField";

interface SurveyRuleConditionFormProps {
  surveyRuleId: string;
  surveyRuleCondition: Instance<typeof SurveyRuleConditionModel>;
}

const KEY_TYPE_FIELD_WIDTH = "280px";

export const SurveyRuleConditionForm: React.FC<SurveyRuleConditionFormProps> =
  observer(({ surveyRuleId, surveyRuleCondition }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();

    const surveyRuleStore = useSurveyRuleStore();

    const { conditionId, keyType } = surveyRuleCondition;

    const keyTypeOptions: DropdownItem[] = [
      {
        name: t("surveys.rules.questionKeyType"),
        value: "QUESTION",
      },
      {
        name: t("surveys.rules.respondentKeyType"),
        value: "RESPONDENT",
      },
    ];

    const handleKeyTypeChange = (selectedItem: DropdownItem): void => {
      if (
        selectedItem.value !== "QUESTION" &&
        selectedItem.value !== "RESPONDENT"
      ) {
        throw new Error(
          `Invalid survey rule key type value: ${selectedItem.value}`,
        );
      }

      surveyRuleStore.setRuleKeyType(
        surveyRuleId,
        conditionId,
        selectedItem.value,
      );
    };

    const handleSourceKeyChange = (
      selectedItem: AutoCompleteItem | null,
    ): void => {
      surveyRuleStore.setRuleKey(
        surveyRuleId,
        conditionId,
        selectedItem?.id ?? undefined,
      );
    };

    const handleSourceValueChange = (
      selectedItems: AutoCompleteItem[],
    ): void => {
      surveyRuleStore.setRuleValue(
        surveyRuleId,
        conditionId,
        selectedItems
          .map((selectedItem) => selectedItem.id)
          .filter((value): value is string => value !== null),
      );
    };

    const handleCommaSeparatedSourceValueChange = (value: string): void => {
      const commaSeparatedValues = value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      surveyRuleStore.setRuleValue(
        surveyRuleId,
        conditionId,
        commaSeparatedValues,
      );
    };

    return (
      <Stack direction="row" gap={spacing.spaceXS}>
        <DropdownInputField
          name="surveyRuleKeyType"
          label={EMPTY_CHARACTER}
          placeholder={t("surveys.rules.keyTypePlaceholder")}
          value={keyType.type}
          options={keyTypeOptions}
          onSelect={handleKeyTypeChange}
          width={KEY_TYPE_FIELD_WIDTH}
          minWidth={KEY_TYPE_FIELD_WIDTH}
        />
        <SurveyRuleKeyInputField
          surveyRuleCondition={surveyRuleCondition}
          onChange={handleSourceKeyChange}
        />
        <SurveyRuleValueInputField
          surveyRuleCondition={surveyRuleCondition}
          onChange={handleSourceValueChange}
          onTextChange={handleCommaSeparatedSourceValueChange}
        />
      </Stack>
    );
  });
